import React, { useCallback, useEffect, useState } from 'react';
import { C, S } from '../../utils/styles';
import Icon from '../Icon';
import { useToast } from '../Toast';
import { listMyCourses, listMyCertificates } from './learnerApi';
import CoursePlayer from './CoursePlayer';
import CertificateView from './CertificateView';

// ----------------------------------------------------------------------------
// MyTrainingHome — entry point for learners (AM, Employer, Employee)
// Lists their available courses + certificates. Drills into the CoursePlayer
// or CertificateView for individual items.
// ----------------------------------------------------------------------------

function statusPillStyle(status) {
  if (status === 'passed')      return { bg: C.greenLight, fg: C.green,   border: C.greenBorder, label: 'Completed' };
  if (status === 'in_progress') return { bg: C.accentBg,   fg: C.accent,  border: C.accent + '33', label: 'In progress' };
  if (status === 'failed')      return { bg: C.redLight,   fg: C.red,     border: C.red + '33',    label: 'Try again' };
  return { bg: C.bg, fg: C.textMid, border: C.border, label: 'Not started' };
}

export default function MyTrainingHome({ embedded = false }) {
  const showToast = useToast();
  const [audience, setAudience] = useState(null);
  const [courses, setCourses] = useState([]);
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCourseId, setOpenCourseId] = useState(null);
  const [openCertId, setOpenCertId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [coursesRes, certsRes] = await Promise.all([listMyCourses(), listMyCertificates()]);
      setAudience(coursesRes.audience);
      setCourses(coursesRes.courses || []);
      setCerts(certsRes || []);
    } catch (err) {
      showToast(err.message || 'Failed to load training', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  if (openCourseId) {
    return (
      <CoursePlayer
        courseId={openCourseId}
        onBack={() => { setOpenCourseId(null); load(); }}
        onCertificateIssued={(certId) => { setOpenCourseId(null); load(); setOpenCertId(certId); }}
      />
    );
  }
  if (openCertId) {
    return (
      <CertificateView
        certificateId={openCertId}
        onBack={() => { setOpenCertId(null); load(); }}
      />
    );
  }

  const wrapperStyle = embedded
    ? { display: 'flex', flexDirection: 'column', gap: 16 }
    : { maxWidth: 1100, margin: '0 auto', padding: '24px 0', display: 'flex', flexDirection: 'column', gap: 16 };

  return (
    <div style={wrapperStyle} data-testid="my-training-home">
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Icon name="school-outline" style={{ fontSize: 32, color: C.accent }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: 0, letterSpacing: '-0.01em' }}>My Training</h1>
            <p style={{ margin: '4px 0 0', color: C.textMid, fontSize: 13 }}>
              Complete your assigned HealthCues courses. Earn a verifiable certificate for every course you pass.
            </p>
          </div>
        </div>
      </div>

      {/* Courses */}
      <div style={S.card}>
        <div style={{ ...S.heading, marginBottom: 12 }}>Courses ({courses.length})</div>
        {loading && <div style={{ color: C.textLight, fontStyle: 'italic', padding: 12 }}>Loading…</div>}
        {!loading && courses.length === 0 && (
          <div style={{ padding: 24, color: C.textLight, fontStyle: 'italic', textAlign: 'center' }}>
            No courses assigned to you yet.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
          {courses.map((c) => {
            const pill = statusPillStyle(c.status);
            return (
              <div
                key={c.id}
                data-testid={`my-course-card-${c.id}`}
                style={{
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  padding: 16,
                  background: '#fff',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  cursor: 'pointer',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 18px rgba(0,0,0,0.05)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
                onClick={() => setOpenCourseId(c.id)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: C.text, lineHeight: 1.3 }}>{c.title}</div>
                    {c.description ? (
                      <div style={{ fontSize: 12, color: C.textMid, marginTop: 2, lineHeight: 1.5 }}>{c.description}</div>
                    ) : null}
                  </div>
                  <span style={{
                    flexShrink: 0,
                    padding: '3px 8px',
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    background: pill.bg, color: pill.fg, border: `1px solid ${pill.border}`,
                  }}>{pill.label}</span>
                </div>
                {c.mustCompleteBeforeAccess && c.status !== 'passed' && (
                  <div style={{ fontSize: 11, color: C.orange, fontWeight: 600 }}>
                    <Icon name="alert-circle-outline" style={{ fontSize: 12, marginRight: 4, verticalAlign: '-1px' }} />
                    Required for portal access
                  </div>
                )}
                {/* Progress bar */}
                <div style={{ marginTop: 4 }}>
                  <div style={{ height: 6, borderRadius: 999, background: C.borderLight, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      width: `${c.lessonProgressPercent}%`,
                      background: c.status === 'passed' ? C.green : C.accent,
                      transition: 'width 0.3s',
                    }} />
                  </div>
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <span>{c.lessonsCompleted}/{c.lessonsTotal} lessons{c.quizQuestionCount ? ` · ${c.quizQuestionCount} quiz` : ''}</span>
                    {c.status === 'passed' && c.bestScore != null ? (
                      <span style={{ color: C.green, fontWeight: 700 }}>{c.bestScore}% · passed</span>
                    ) : c.bestScore > 0 ? (
                      <span>Best {c.bestScore}%</span>
                    ) : null}
                  </div>
                </div>
                <button
                  type="button"
                  data-testid={`open-course-${c.id}`}
                  onClick={(e) => { e.stopPropagation(); setOpenCourseId(c.id); }}
                  style={{ ...S.buttonPrimary, marginTop: 6, fontSize: 12 }}
                >
                  {c.status === 'passed' ? 'Review course' : c.status === 'in_progress' ? 'Resume' : 'Start course'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Certificates */}
      {certs.length > 0 && (
        <div style={S.card}>
          <div style={{ ...S.heading, marginBottom: 12 }}>My Certificates ({certs.length})</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {certs.map((ct) => (
              <button
                key={ct.id}
                type="button"
                data-testid={`open-cert-${ct.id}`}
                onClick={() => setOpenCertId(ct.id)}
                style={{
                  border: `1px solid ${C.greenBorder}`,
                  background: C.greenLight,
                  borderRadius: 12,
                  padding: 14,
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon name="ribbon-outline" style={{ fontSize: 22, color: C.green }} />
                  <span style={{ fontSize: 11, color: C.green, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Certificate · Verified</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{ct.courseTitle}</div>
                <div style={{ fontSize: 11, color: C.textMid, fontFamily: "'JetBrains Mono', monospace" }}>
                  {ct.id} · {ct.scorePercent}%
                </div>
                <div style={{ fontSize: 11, color: C.textLight }}>
                  Issued {new Date(ct.issuedAt).toLocaleDateString()}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
